import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { generateSessionToken, isValidSessionToken, checkRateLimit } from '../utils/security';
import { db, doc, getDoc, setDoc, getDocs, updateDoc, onSnapshot, query, orderBy } from '../utils/firebase';
import { collection } from 'firebase/firestore';
export interface User {
  id: string;
  phone: string;
  countryCode: string;
  fullName: string;
  passwordHash: string;
  createdAt: string;
  purchasedFiles: string[];
}
interface AuthContextType {
  currentUser: User | null;
  isAdmin: boolean;
  login: (phone: string, countryCode: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  signup: (phone: string, countryCode: string, fullName: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  adminLogin: (email: string, password: string, remember?: boolean) => {
    success: boolean;
    error?: string;
  };
  logout: () => void;
  getUsers: () => Promise<User[]>;
  grantAccess: (userId: string, fileId: string) => void;
  refreshCurrentUser: () => void;
  updateProfile: (fullName: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
}
const ADMIN_EMAIL = 'theobayavuge1@gmail.com';
const ADMIN_PASSWORD = 'Teo@43211';
// === STRONGER PASSWORD HASHING ===
// Multi-round hash with salt for better security
function strongHash(password: string, salt?: string): string {
  const useSalt = salt || generateSalt();
  let hash = useSalt + password;
  // 1000 rounds of hashing
  for (let round = 0; round < 1000; round++) {
    let h = 0x811c9dc5; // FNV offset basis
    for (let i = 0; i < hash.length; i++) {
      h ^= hash.charCodeAt(i);
      h = Math.imul(h, 0x01000193); // FNV prime
    }
    hash = useSalt + Math.abs(h).toString(36) + round.toString(36);
  }
  // Final hash
  let finalHash = 0;
  for (let i = 0; i < hash.length; i++) {
    const char = hash.charCodeAt(i);
    finalHash = (finalHash << 5) - finalHash + char | 0;
  }
  return 'sh_' + useSalt + '_' + Math.abs(finalHash).toString(36) + '_' + btoa(hash.slice(0, 12)).slice(0, 16);
}
function generateSalt(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let salt = '';
  for (let i = 0; i < 8; i++) {
    salt += chars[Math.floor(Math.random() * chars.length)];
  }
  return salt;
}
function extractSalt(hash: string): string | null {
  if (hash.startsWith('sh_')) {
    const parts = hash.split('_');
    return parts[1] || null;
  }
  return null;
}
// Legacy hash for backward compatibility
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return 'h_' + Math.abs(hash).toString(36) + '_' + btoa(str).slice(0, 8);
}
// Verify password against hash (supports both old and new format)
function verifyPassword(password: string, storedHash: string): boolean {
  // Try new strong hash first
  if (storedHash.startsWith('sh_')) {
    const salt = extractSalt(storedHash);
    if (salt) {
      return strongHash(password, salt) === storedHash;
    }
  }
  // Fall back to legacy hash
  return simpleHash(password) === storedHash;
}
const usersCollection = collection(db, 'users');
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem('edumarket_current_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [isAdmin, setIsAdmin] = useState(() => {
    try {
      const rememberToken = localStorage.getItem('edumarket_admin_remember');
      if (rememberToken && isValidSessionToken(rememberToken)) {
        return true;
      }
      return localStorage.getItem('edumarket_is_admin') === 'true';
    } catch {
      return false;
    }
  });
  // Persist currentUser to localStorage
  useEffect(() => {
    try {
      if (currentUser) {
        localStorage.setItem('edumarket_current_user', JSON.stringify(currentUser));
      } else {
        localStorage.removeItem('edumarket_current_user');
      }
    } catch {}
  }, [currentUser]);
  // Persist isAdmin to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('edumarket_is_admin', String(isAdmin));
    } catch {}
  }, [isAdmin]);
  // Listen for current user changes in Firestore
  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.id), snap => {
      if (snap.exists()) {
        const userData = {
          id: snap.id,
          ...snap.data()
        } as User;
        if (JSON.stringify(userData.purchasedFiles) !== JSON.stringify(currentUser.purchasedFiles) || userData.fullName !== currentUser.fullName) {
          setCurrentUser(userData);
        }
      }
    }, () => {});
    return () => unsub();
  }, [currentUser?.id]);
  // Poll localStorage as fallback
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(() => {
      try {
        const raw = localStorage.getItem('edumarket_current_user');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (JSON.stringify(parsed.purchasedFiles) !== JSON.stringify(currentUser.purchasedFiles)) {
            setCurrentUser(parsed);
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [currentUser]);
  const getUsers = useCallback(async (): Promise<User[]> => {
    try {
      const snapshot = await getDocs(query(usersCollection, orderBy('createdAt', 'asc')));
      const users = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as User);
      try {
        localStorage.setItem('edumarket_users', JSON.stringify(users));
      } catch {}
      return users;
    } catch {
      try {
        const data = localStorage.getItem('edumarket_users');
        return data ? JSON.parse(data) : [];
      } catch {
        return [];
      }
    }
  }, []);
  const refreshCurrentUser = useCallback(async () => {
    if (!currentUser) return;
    try {
      const snap = await getDoc(doc(db, 'users', currentUser.id));
      if (snap.exists()) {
        setCurrentUser({
          id: snap.id,
          ...snap.data()
        } as User);
      }
    } catch {
      try {
        const raw = localStorage.getItem('edumarket_current_user');
        if (raw) setCurrentUser(JSON.parse(raw));
      } catch {}
    }
  }, [currentUser]);
  const signup = useCallback(async (phone: string, countryCode: string, fullName: string, password: string): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const rateCheck = checkRateLimit('signup_' + phone, 5, 300000);
    if (!rateCheck.allowed) {
      return {
        success: false,
        error: 'Too many signup attempts. Please try again later.'
      };
    }
    try {
      const snapshot = await getDocs(usersCollection);
      const users = snapshot.docs.map(d => d.data());
      const existing = users.find((u: any) => u.phone === phone && u.countryCode === countryCode);
      if (existing) return {
        success: false,
        error: 'Phone number already registered'
      };
    } catch {
      try {
        const localUsers = JSON.parse(localStorage.getItem('edumarket_users') || '[]');
        const existing = localUsers.find((u: any) => u.phone === phone && u.countryCode === countryCode);
        if (existing) return {
          success: false,
          error: 'Phone number already registered'
        };
      } catch {}
    }
    const newUser: User = {
      id: 'user_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      phone,
      countryCode,
      fullName,
      passwordHash: strongHash(password),
      createdAt: new Date().toISOString(),
      purchasedFiles: []
    };
    try {
      await setDoc(doc(db, 'users', newUser.id), newUser);
    } catch {
      try {
        const localUsers = JSON.parse(localStorage.getItem('edumarket_users') || '[]');
        localUsers.push(newUser);
        localStorage.setItem('edumarket_users', JSON.stringify(localUsers));
      } catch {}
    }
    setCurrentUser(newUser);
    setIsAdmin(false);
    try {
      const notifId = 'notif_' + Date.now();
      await setDoc(doc(db, 'notifications', notifId), {
        id: notifId,
        type: 'new_user',
        message: `New user registered: ${fullName} (${countryCode}${phone})`,
        data: {
          userId: newUser.id,
          fullName,
          phone,
          countryCode
        },
        read: false,
        createdAt: new Date().toISOString()
      });
    } catch {}
    return {
      success: true
    };
  }, []);
  const login = useCallback(async (phone: string, countryCode: string, password: string): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      const snapshot = await getDocs(usersCollection);
      const users = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as User);
      const user = users.find(u => u.phone === phone && u.countryCode === countryCode);
      if (!user) return {
        success: false,
        error: 'User not found'
      };
      if (!verifyPassword(password, user.passwordHash)) return {
        success: false,
        error: 'Invalid password'
      };
      // Upgrade old hash to strong hash on successful login
      if (user.passwordHash.startsWith('h_')) {
        const newHash = strongHash(password);
        try {
          await updateDoc(doc(db, 'users', user.id), {
            passwordHash: newHash
          });
          user.passwordHash = newHash;
        } catch {}
      }
      setCurrentUser(user);
      setIsAdmin(false);
      return {
        success: true
      };
    } catch {
      try {
        const localUsers = JSON.parse(localStorage.getItem('edumarket_users') || '[]');
        const user = localUsers.find((u: any) => u.phone === phone && u.countryCode === countryCode);
        if (!user) return {
          success: false,
          error: 'User not found'
        };
        if (!verifyPassword(password, user.passwordHash)) return {
          success: false,
          error: 'Invalid password'
        };
        setCurrentUser(user);
        setIsAdmin(false);
        return {
          success: true
        };
      } catch {
        return {
          success: false,
          error: 'Login error'
        };
      }
    }
  }, []);
  const adminLogin = useCallback((email: string, password: string, remember?: boolean) => {
    try {
      const lockoutData = localStorage.getItem('edumarket_admin_lockout');
      if (lockoutData) {
        const lockout = JSON.parse(lockoutData);
        if (lockout.lockedUntil && Date.now() < lockout.lockedUntil) {
          const remaining = Math.ceil((lockout.lockedUntil - Date.now()) / 60000);
          return {
            success: false,
            error: `Account locked. Try again in ${remaining} minute(s).`
          };
        }
        if (lockout.lockedUntil && Date.now() >= lockout.lockedUntil) {
          localStorage.removeItem('edumarket_admin_lockout');
        }
      }
      if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
        localStorage.removeItem('edumarket_admin_lockout');
        setIsAdmin(true);
        setCurrentUser(null);
        if (remember) {
          const token = generateSessionToken();
          localStorage.setItem('edumarket_admin_remember', token);
        }
        return {
          success: true
        };
      }
      const existing = lockoutData ? JSON.parse(lockoutData) : {
        attempts: 0
      };
      existing.attempts += 1;
      if (existing.attempts >= 5) {
        existing.lockedUntil = Date.now() + 5 * 60 * 1000;
        existing.attempts = 0;
      }
      localStorage.setItem('edumarket_admin_lockout', JSON.stringify(existing));
      const attemptsLeft = 5 - existing.attempts;
      return {
        success: false,
        error: `Invalid credentials. ${attemptsLeft > 0 ? `${attemptsLeft} attempts remaining.` : 'Account locked for 5 minutes.'}`
      };
    } catch {
      return {
        success: false,
        error: 'Login error'
      };
    }
  }, []);
  const logout = useCallback(() => {
    setCurrentUser(null);
    setIsAdmin(false);
    try {
      localStorage.removeItem('edumarket_current_user');
      localStorage.removeItem('edumarket_is_admin');
      localStorage.removeItem('edumarket_admin_remember');
    } catch {}
  }, []);
  const grantAccess = useCallback(async (userId: string, fileId: string) => {
    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const purchasedFiles = userData.purchasedFiles || [];
        if (!purchasedFiles.includes(fileId)) {
          purchasedFiles.push(fileId);
          await updateDoc(doc(db, 'users', userId), {
            purchasedFiles
          });
        }
      }
    } catch {}
    try {
      const currentRaw = localStorage.getItem('edumarket_current_user');
      if (currentRaw) {
        const current = JSON.parse(currentRaw);
        if (current.id === userId && !current.purchasedFiles.includes(fileId)) {
          current.purchasedFiles.push(fileId);
          localStorage.setItem('edumarket_current_user', JSON.stringify(current));
        }
      }
    } catch {}
    if (currentUser?.id === userId) {
      setCurrentUser(prev => prev ? {
        ...prev,
        purchasedFiles: [...new Set([...prev.purchasedFiles, fileId])]
      } : prev);
    }
  }, [currentUser]);
  // === NEW: Update user profile name ===
  const updateProfile = useCallback(async (fullName: string): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (!currentUser) return {
      success: false,
      error: 'Not logged in'
    };
    if (!fullName.trim()) return {
      success: false,
      error: 'Name cannot be empty'
    };
    try {
      await updateDoc(doc(db, 'users', currentUser.id), {
        fullName: fullName.trim()
      });
      setCurrentUser(prev => prev ? {
        ...prev,
        fullName: fullName.trim()
      } : prev);
      return {
        success: true
      };
    } catch {
      try {
        const updated = {
          ...currentUser,
          fullName: fullName.trim()
        };
        localStorage.setItem('edumarket_current_user', JSON.stringify(updated));
        setCurrentUser(updated);
        return {
          success: true
        };
      } catch {
        return {
          success: false,
          error: 'Update failed'
        };
      }
    }
  }, [currentUser]);
  // === NEW: Change password ===
  const changePassword = useCallback(async (oldPassword: string, newPassword: string): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (!currentUser) return {
      success: false,
      error: 'Not logged in'
    };
    if (newPassword.length < 4) return {
      success: false,
      error: 'Password must be at least 4 characters'
    };
    // Verify old password
    if (!verifyPassword(oldPassword, currentUser.passwordHash)) {
      return {
        success: false,
        error: 'Current password is incorrect'
      };
    }
    const newHash = strongHash(newPassword);
    try {
      await updateDoc(doc(db, 'users', currentUser.id), {
        passwordHash: newHash
      });
      setCurrentUser(prev => prev ? {
        ...prev,
        passwordHash: newHash
      } : prev);
      return {
        success: true
      };
    } catch {
      try {
        const updated = {
          ...currentUser,
          passwordHash: newHash
        };
        localStorage.setItem('edumarket_current_user', JSON.stringify(updated));
        setCurrentUser(updated);
        return {
          success: true
        };
      } catch {
        return {
          success: false,
          error: 'Password change failed'
        };
      }
    }
  }, [currentUser]);
  return <AuthContext.Provider value={{
    currentUser,
    isAdmin,
    login,
    signup,
    adminLogin,
    logout,
    getUsers,
    grantAccess,
    refreshCurrentUser,
    updateProfile,
    changePassword
  }} data-id="element-185">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}